/**
 * Multi-select on the video grid: what a selection holds and the pure
 * operations the page applies to it. Kept apart from the component so the
 * range and page rules are testable without a DOM.
 */

import type { VideoStatus } from '@/components/ds/StatusChip'

/**
 * What a bulk action needs to know about a selected video. It's a snapshot,
 * not a key, so a selection can outlive the grid page it was made on — the
 * bulk actions still have the path and channel of cards no longer on screen.
 */
export interface SelectedVideo {
  id: string
  language: string
  /** Channel slug — the archive DELETE resolves the folder from it. */
  channel: string
  title: string
  status: VideoStatus
  /** Relative ``output/...`` MP4 path; empty when there is no MP4. */
  videoPath: string
  /** YouTube views when the snapshot was taken; null when there is no count. */
  views: number | null
  /** YouTube id do upload; vazio quando o vídeo não subiu (ou subiu ``manual``). */
  youtubeId: string
  /** Visibilidade real no YouTube; null quando ainda não lida (ou sem upload). */
  youtubePrivacy: YoutubePrivacy | null
}

export type YoutubePrivacy = 'public' | 'unlisted' | 'private'

/**
 * Quem a troca de visibilidade em massa alcança. Só vídeo publicado com id do
 * YouTube; agendado fica de fora (a v1 não mexe em agendamento), e o resto não
 * está no YouTube. Um id por item: o mesmo vídeo do YouTube pode estar em dois
 * projetos, e a visibilidade é dele, não do projeto.
 */
export function visibilityTargets(selected: readonly SelectedVideo[]): {
  eligible: SelectedVideo[]
  scheduled: number
  unpublished: number
} {
  const eligible: SelectedVideo[] = []
  const seen = new Set<string>()
  let scheduled = 0
  let unpublished = 0
  for (const v of selected) {
    if (v.status === 'agendado') {
      scheduled += 1
    } else if (v.status !== 'publicado' || !v.youtubeId || v.youtubeId === 'manual') {
      unpublished += 1
    } else if (!seen.has(v.youtubeId)) {
      seen.add(v.youtubeId)
      eligible.push(v)
    }
  }
  return { eligible, scheduled, unpublished }
}

/**
 * O backend sabe trocar a visibilidade? Só o novo manda a chave
 * ``youtube_privacy`` (mesmo nula) no upload e no ``/youtube``. No intervalo do
 * deploy em dois tempos (front no merge, back no pull) a tela fica como antes.
 */
export function canChangeVisibility(source: object | null | undefined): boolean {
  return source != null && 'youtube_privacy' in source
}

export type Selection = ReadonlyMap<string, SelectedVideo>

/** ``(id, language)`` is the video's identity — the same id exists in pt-br and en. */
export function selectionKey(video: Pick<SelectedVideo, 'id' | 'language'>): string {
  return `${video.language}/${video.id}`
}

/**
 * A card mid-generation (or mid-upload) has neither a finished MP4 to
 * download nor a folder that is safe to delete, so it can't be selected.
 */
export function isSelectable(status: VideoStatus): boolean {
  return status !== 'gerando'
}

export function toggleOne(selection: Selection, video: SelectedVideo): Map<string, SelectedVideo> {
  const next = new Map(selection)
  const key = selectionKey(video)
  if (next.has(key)) next.delete(key)
  else if (isSelectable(video.status)) next.set(key, video)
  return next
}

/**
 * Shift-click: every card between the anchor and the target, in page order
 * and inclusive, takes ``select`` — adding skips the unselectable ones,
 * removing doesn't need to. Selections made on other pages are left alone.
 * Falls back to a plain toggle when the anchor isn't on this page (it was
 * clicked before a page change).
 */
export function applyRange(
  selection: Selection,
  pageVideos: readonly SelectedVideo[],
  anchorKey: string,
  target: SelectedVideo,
  select: boolean,
): Map<string, SelectedVideo> {
  const keys = pageVideos.map(selectionKey)
  const from = keys.indexOf(anchorKey)
  const to = keys.indexOf(selectionKey(target))
  if (from === -1 || to === -1) return toggleOne(selection, target)

  const next = new Map(selection)
  for (const video of pageVideos.slice(Math.min(from, to), Math.max(from, to) + 1)) {
    if (!select) next.delete(selectionKey(video))
    else if (isSelectable(video.status)) next.set(selectionKey(video), video)
  }
  return next
}

export function selectAll(selection: Selection, videos: readonly SelectedVideo[]): Map<string, SelectedVideo> {
  const next = new Map(selection)
  for (const video of videos) {
    if (isSelectable(video.status)) next.set(selectionKey(video), video)
  }
  return next
}

export function deselectAll(selection: Selection, videos: readonly SelectedVideo[]): Map<string, SelectedVideo> {
  const next = new Map(selection)
  for (const video of videos) next.delete(selectionKey(video))
  return next
}

/**
 * O resumo da barra de seleção, sobre todas as páginas: quantos estão no ar,
 * quantos agendados, e a soma das views dos que estão no ar (a mesma regra
 * do card, que só mostra views de vídeo publicado).
 */
export function selectionSummary(videos: readonly SelectedVideo[]): {
  published: number
  scheduled: number
  views: number
} {
  let published = 0
  let scheduled = 0
  let views = 0
  for (const v of videos) {
    if (v.status === 'agendado') scheduled += 1
    if (v.status !== 'publicado') continue
    published += 1
    views += v.views ?? 0
  }
  return { published, scheduled, views }
}

export function withoutKeys(selection: Selection, keys: Iterable<string>): Map<string, SelectedVideo> {
  const next = new Map(selection)
  for (const key of keys) next.delete(key)
  return next
}

// Mesmo corte do backend (``media/routes.py::_download_filename``): o que o
// Windows recusa em nome de arquivo, mais os caracteres de controle.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_FILENAME_CHARS = /[\u0000-\u001f\u007f<>:"/\\|?*]/g
const MAX_FILENAME_STEM = 120

/**
 * File name for a downloaded MP4: the video title, minus the characters no
 * file system accepts, capped and ending in ``.mp4``. ``fallback`` (the
 * video id) covers titles that sanitise down to nothing.
 */
export function downloadFilename(title: string, fallback: string): string {
  const clean = (text: string) =>
    text.replace(FORBIDDEN_FILENAME_CHARS, '').replace(/\s+/g, ' ').trim().slice(0, MAX_FILENAME_STEM)
  const stem = clean(title).replace(/^[\s.]+|[\s.]+$/g, '')
  return `${stem || clean(fallback) || 'video'}.mp4`
}
